import {
	AccessibilityHelp,
	Alignment,
	AutoImage,
	AutoLink,
	Autoformat,
	Autosave,
	BlockQuote,
	Bold,
	Code,
	CodeBlock,
	Enter,
	Essentials,
	FindAndReplace,
	FontBackgroundColor,
	FontColor,
	FontFamily,
	FontSize,
	GeneralHtmlSupport,
	Heading,
	Highlight,
	HorizontalLine,
	HtmlEmbed,
	ImageBlock,
	ImageCaption,
	ImageInline,
	ImageInsert,
	ImageInsertViaUrl,
	ImageResize,
	ImageStyle,
	ImageTextAlternative,
	ImageToolbar,
	ImageUpload,
	Indent,
	IndentBlock,
	Italic,
	Link,
	LinkImage,
	List,
	ListProperties,
	MediaEmbed,
	Paragraph,
	PasteFromOffice,
	PastePlainText,
	RemoveFormat,
	SelectAll,
	ShiftEnter,
	SimpleUploadAdapter,
	SourceEditing,
	SpecialCharacters,
	SpecialCharactersArrows,
	SpecialCharactersCurrency,
	SpecialCharactersEssentials,
	SpecialCharactersLatin,
	SpecialCharactersMathematical,
	SpecialCharactersText,
	Strikethrough,
	Style,
	Subscript,
	Superscript,
	Table,
	TableCaption,
	TableCellProperties,
	TableColumnResize,
	TableProperties,
	TableToolbar,
	TextTransformation,
	TodoList,
	Underline,
	Undo,
} from 'ckeditor5';

import ShortcodePlugin from './ShortcodePlugin';
import uploadPlugin from './uploadPlugin';

export const baseEditorConfig = {
	toolbar: {
		items: ['bold', 'italic', 'underline', 'link'],
		shouldNotGroupWhenFull: false,
	},
	plugins: [
		Autosave,
		Essentials,
		Paragraph,
		Bold,
		Italic,
		Underline,
		Link,
		Undo,
		Enter,
		ShiftEnter,
	],
	menuBar: {
		isVisible: true,
	},
	placeholder: 'Type or paste your content here!',

	link: {
		addTargetToExternalLinks: true,
		defaultProtocol: 'https://',
	},
};

export const fullEditorConfig = {
	...baseEditorConfig,
	toolbar: {
		items: [
			...baseEditorConfig.toolbar.items,
			'shortcodeDropdown',
			'sourceEditing',
			'|',
			'undo',
			'redo',
			'|',
			'findAndReplace',
			'selectAll',
			'|',
			'heading',
			'style',
			'|',
			'fontSize',
			'fontFamily',
			'fontColor',
			'fontBackgroundColor',
			'|',
			'strikethrough',
			'subscript',
			'superscript',
			'code',
			'removeFormat',
			'|',
			'specialCharacters',
			'horizontalLine',
			'insertImage',
			'mediaEmbed',
			'insertTable',
			'highlight',
			'blockQuote',
			'codeBlock',
			'htmlEmbed',
			'|',
			'alignment',
			'|',
			'bulletedList',
			'numberedList',
			'todoList',
			'outdent',
			'indent',
			'|',
			'accessibilityHelp',
		],
		shouldNotGroupWhenFull: false,
	},
	plugins: [
		...baseEditorConfig.plugins,
		AccessibilityHelp,
		Alignment,
		Autoformat,
		AutoImage,
		AutoLink,
		BlockQuote,
		Code,
		CodeBlock,
		FindAndReplace,
		FontBackgroundColor,
		FontColor,
		FontFamily,
		FontSize,
		GeneralHtmlSupport,
		Heading,
		Highlight,
		HorizontalLine,
		HtmlEmbed,
		ImageBlock,
		ImageCaption,
		ImageInline,
		ImageInsert,
		ImageInsertViaUrl,
		ImageResize,
		ImageStyle,
		ImageTextAlternative,
		ImageToolbar,
		ImageUpload,
		Indent,
		IndentBlock,
		LinkImage,
		List,
		ListProperties,
		MediaEmbed,
		PasteFromOffice,
		RemoveFormat,
		SelectAll,
		SimpleUploadAdapter,
		SourceEditing,
		SpecialCharacters,
		SpecialCharactersArrows,
		SpecialCharactersCurrency,
		SpecialCharactersEssentials,
		SpecialCharactersLatin,
		SpecialCharactersMathematical,
		SpecialCharactersText,
		Strikethrough,
		Style,
		Subscript,
		Superscript,
		Table,
		TableCaption,
		TableCellProperties,
		TableColumnResize,
		TableProperties,
		TableToolbar,
		TextTransformation,
		TodoList,
		PastePlainText,
	],
	fontFamily: {
		supportAllValues: true,
	},
	fontSize: {
		options: [10, 12, 14, 'default', 18, 20, 22],
		supportAllValues: true,
	},
	image: {
		toolbar: [
			'toggleImageCaption',
			'imageTextAlternative',
			'|',
			'imageStyle:inline',
			'imageStyle:wrapText',
			'imageStyle:breakText',
			'|',
			'resizeImage',
		],
	},
	link: {
		addTargetToExternalLinks: true,
		defaultProtocol: 'https://',
		decorators: {
			toggleDownloadable: {
				mode: 'manual',
				label: 'Downloadable',
				attributes: {
					download: 'file',
				},
			},
		},
	},
	list: {
		properties: {
			styles: true,
			startIndex: true,
			reversed: true,
		},
	},
	menuBar: {
		isVisible: true,
	},
	placeholder: 'Type or paste your content here!',
	style: {
		definitions: [
			{
				name: 'Article category',
				element: 'h3',
				classes: ['category'],
			},
			{
				name: 'Title',
				element: 'h2',
				classes: ['document-title'],
			},
			{
				name: 'Subtitle',
				element: 'h3',
				classes: ['document-subtitle'],
			},
			{
				name: 'Info box',
				element: 'p',
				classes: ['info-box'],
			},
			{
				name: 'Side quote',
				element: 'blockquote',
				classes: ['side-quote'],
			},
			{
				name: 'Marker',
				element: 'span',
				classes: ['marker'],
			},
			{
				name: 'Spoiler',
				element: 'span',
				classes: ['spoiler'],
			},
			{
				name: 'Code (dark)',
				element: 'pre',
				classes: ['fancy-code', 'fancy-code-dark'],
			},
			{
				name: 'Code (bright)',
				element: 'pre',
				classes: ['fancy-code', 'fancy-code-bright'],
			},
		],
	},
	table: {
		contentToolbar: [
			'tableColumn',
			'tableRow',
			'mergeTableCells',
			'tableProperties',
			'tableCellProperties',
		],
	},
	extraPlugins: [uploadPlugin, ShortcodePlugin],
	htmlSupport: {
		allow: [
			{ name: 'iframe', attributes: true, classes: true, styles: true },
			{
				name: 'span',
				attributes: true,
				classes: true,
				styles: true,
			},
		],
	},
	mediaEmbed: { previewsInData: true },
};

export const limitedEditorConfig = {
	...baseEditorConfig,
	menuBar: {
		isVisible: false,
	},
	forcePasteAsPlainText: true,
	enterMode: 2,
	autoParagraph: false,
};
